import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Clock, Power, PowerOff, Calendar, DollarSign, Server,
  Play, Pause, Plus, Trash2, Edit, Check, X, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResourceScheduler = () => {
  const { shouldShowData } = useDemoData();
  const [schedules, setSchedules] = useState([]);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    resources: '',
    startTime: '08:00',
    stopTime: '18:00',
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    enabled: true
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/schedules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSchedules(data);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const totalSavings = schedules.filter(s => s.enabled).reduce((sum, s) => sum + s.monthlySavings, 0);
  const totalResources = schedules.reduce((sum, s) => sum + s.resourceCount, 0);
  const activeSchedules = schedules.filter(s => s.enabled).length;

  const toggleSchedule = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/schedules/${id}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSchedules();
      toast.success('Schedule updated');
    } catch (error) {
      console.error('Error toggling schedule:', error);
      toast.error('Failed to update schedule');
    }
  };

  const deleteSchedule = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/schedules/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSchedules();
      toast.success('Schedule deleted');
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const handleAddSchedule = async () => {
    if (!newSchedule.name || !newSchedule.resources) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/schedules`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newSchedule.name,
          resources: newSchedule.resources,
          start_time: newSchedule.startTime,
          stop_time: newSchedule.stopTime,
          days: newSchedule.days,
          enabled: newSchedule.enabled
        })
      });
      setShowAddSchedule(false);
      setNewSchedule({
        name: '',
        resources: '',
        startTime: '08:00',
        stopTime: '18:00',
        days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
        enabled: true
      });
      fetchSchedules();
      toast.success('Schedule created');
    } catch (error) {
      console.error('Error creating schedule:', error);
      toast.error('Failed to create schedule');
    }
  };

  const toggleDay = (day) => {
    if (newSchedule.days.includes(day)) {
      setNewSchedule({ ...newSchedule, days: newSchedule.days.filter(d => d !== day) });
    } else {
      setNewSchedule({ ...newSchedule, days: [...newSchedule.days, day] });
    }
  };

  if (!shouldShowData) return <EmptyDataState title="Resource Scheduler" description="Connect your cloud accounts to schedule resource start/stop and save costs." />;
  return (
    <div className="space-y-6" data-testid="resource-scheduler-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
            <Clock className="w-7 h-7 text-emerald-500" />
            Resource Scheduler
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Schedule start/stop times to save money on non-production resources
          </p>
        </div>
        <Button onClick={() => setShowAddSchedule(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="w-4 h-4 mr-2" />
          New Schedule
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-800 bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">Monthly Savings</p>
                <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">${totalSavings.toLocaleString()}</p>
              </div>
              <DollarSign className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Scheduled Resources</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{totalResources}</p>
              </div>
              <Server className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Active Schedules</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">{activeSchedules}</p>
              </div>
              <Calendar className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Annual Savings</p>
                <p className="text-2xl font-bold text-emerald-600">${(totalSavings * 12).toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">Save up to 70% on non-production costs</p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                Dev and test environments often run 24/7 but are only used 8-10 hours a day. 
                Schedule them to auto-stop after work hours and weekends.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schedules List */}
      <div className="space-y-4">
        {schedules.map((schedule) => (
          <Card key={schedule.id} className={`border-slate-200 dark:border-slate-800 ${!schedule.enabled ? 'opacity-60' : ''}`}>
            <CardContent className="py-4">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    schedule.status === 'running' 
                      ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                      : 'bg-slate-100 dark:bg-slate-800'
                  }`}>
                    {schedule.status === 'running' 
                      ? <Power className="w-6 h-6 text-emerald-600" />
                      : <PowerOff className="w-6 h-6 text-slate-400" />
                    }
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                      {schedule.name}
                      <Badge variant={schedule.status === 'running' ? 'default' : 'secondary'} className={
                        schedule.status === 'running' ? 'bg-emerald-100 text-emerald-800' : ''
                      }>
                        {schedule.status}
                      </Badge>
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">{schedule.resources}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <span className="text-xs text-slate-400 dark:text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {schedule.startTime} - {schedule.stopTime}
                      </span>
                      <div className="flex gap-1">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <span 
                            key={day}
                            className={`text-xs px-1.5 py-0.5 rounded ${
                              schedule.days.includes(day)
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300'
                                : 'bg-slate-100 text-slate-400 dark:bg-slate-800'
                            }`}
                          >
                            {day}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-slate-400">Monthly Savings</p>
                    <p className="text-lg font-bold text-emerald-600">${schedule.monthlySavings.toLocaleString()}</p>
                  </div>
                  <div className="text-right min-w-[100px]">
                    <p className="text-sm text-slate-400">Next Action</p>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{schedule.nextAction}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch 
                      checked={schedule.enabled}
                      onCheckedChange={() => toggleSchedule(schedule.id)}
                    />
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => deleteSchedule(schedule.id)}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Schedule Modal */}
      {showAddSchedule && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Create Schedule</CardTitle>
              <CardDescription>Schedule automatic start/stop for resources</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Schedule Name *</Label>
                <Input
                  value={newSchedule.name}
                  onChange={(e) => setNewSchedule({ ...newSchedule, name: e.target.value })}
                  placeholder="e.g., Dev Environment"
                />
              </div>
              <div>
                <Label>Resources *</Label>
                <Input
                  value={newSchedule.resources}
                  onChange={(e) => setNewSchedule({ ...newSchedule, resources: e.target.value })}
                  placeholder="e.g., 5 EC2 instances, 2 RDS databases"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    value={newSchedule.startTime}
                    onChange={(e) => setNewSchedule({ ...newSchedule, startTime: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Stop Time</Label>
                  <Input
                    type="time"
                    value={newSchedule.stopTime}
                    onChange={(e) => setNewSchedule({ ...newSchedule, stopTime: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Active Days</Label>
                <div className="flex gap-2 mt-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <button
                      key={day}
                      onClick={() => toggleDay(day)}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        newSchedule.days.includes(day)
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-400'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowAddSchedule(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleAddSchedule} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Create Schedule
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ResourceScheduler;
